import { WButton } from "@/components/button/WButton.tsx";
import { UnconvertedCurrencyBalance } from "@/components/l10n/Currency.tsx";
import { NoticeBlock } from "@/components/NoticeBlock.tsx";
import { siteCurrencyAtom } from "@/config.ts";
import { getCalculatedFees } from "@/modules/billing/payment/fees.ts";
import { PaymentResult, type PaymentProps } from "@/modules/billing/payment/PaymentParams.ts";
import { invalidatePaymentQueries } from "@/modules/billing/payment/paymentUtils.ts";
import type { MethodFees } from "@/modules/billing/paymentMethods.ts";
import { Trans } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai/react";
import { useImperativeHandle } from "react";

export default function EvcHormuudPaymentForm({
    actionsRef,
    account,
    amount,
    fee,
}: PaymentProps & { fee: MethodFees }) {
    //#region Hooks
    const queryClient = useQueryClient();
    const siteCurrency = useAtomValue(siteCurrencyAtom);

    useImperativeHandle(actionsRef, () => ({
        needsPopUp() {
            return false;
        },

        // eslint-disable-next-line @typescript-eslint/require-await
        async makePayment() {
            // Payment is made outside of Warren, there is nothing to start here
            return PaymentResult.CLOSE_MANUALLY;
        },
    }));
    //#endregion

    const billingAccountId = account.id;
    // Payer pays in site currency, same "Total charge" as in the fees table
    const { total } = getCalculatedFees(fee, amount, account.account.vat_percentage);
    const chargeAmount = <UnconvertedCurrencyBalance value={total} currency={siteCurrency} />;
    const creditAmount = <UnconvertedCurrencyBalance value={amount} currency={siteCurrency} />;

    return (
        <NoticeBlock icon="jp-evc-hormuud-icon">
            <p>
                <Trans>
                    Pay the total charge amount of <b>{chargeAmount}</b> with EVC Mobile Money to get {creditAmount}{" "}
                    credits. Enter your billing account ID <b>{billingAccountId}</b> as the payment reference.
                </Trans>
            </p>
            <p>
                <Trans>
                    Credit is added to this billing account automatically once the payment is received. Make sure the
                    billing account ID is entered correctly.
                </Trans>
            </p>
            <div className="pt-2">
                <WButton
                    color="primary"
                    variant="border"
                    icon="jp-icon-refresh"
                    action={async () => await invalidatePaymentQueries(queryClient, billingAccountId)}
                >
                    <Trans>Refresh credit</Trans>
                </WButton>
            </div>
        </NoticeBlock>
    );
}
